import {Modal, ModalBody, ModalFooter, ModalOverlay, ModalTitle} from '@components/ui/modal-atoms/ModalAtoms.tsx'
import {ChangeEvent, FC, ReactNode, useState} from 'react'
import {Trans, useTranslation} from '@/translations/i18n.tsx'
import {Button} from '@components/ui/button/Button.tsx'
import {debounce} from '@utilities/helpers.ts'
import {StyledFullName, StyledModalHeader, StyledModalParagraph} from '@components/commons/delete-modal/style.ts'
import {InputText} from '@components/commons/input-text/InputText.tsx'
import {Spinner} from '@components/ui/spinner/Spinner.tsx'
import {UseMutationResult} from '@tanstack/react-query'
import {AxiosResponse} from 'axios'

interface DeleteModal {
    onClose: () => void
    canBeDeleted: boolean
    paragraph1: string
    deniedTitle: string
    deniedParagraph: ReactNode
    title: string
    fullName: string
    deleteMutation: UseMutationResult<AxiosResponse<unknown, unknown>, Error, void, unknown>
}

export const DeleteModal: FC<DeleteModal> = ({
    deleteMutation,
    onClose,
    title,
    paragraph1,
    canBeDeleted,
    fullName,
    deniedTitle,
    deniedParagraph
}) => {
    const {t} = useTranslation()
    const [isConfirmationValid, setIsConfirmationValid] = useState(false)

    if (!canBeDeleted) {
        return (
            <>
                <ModalOverlay
                    onClick={event => {
                        event.stopPropagation()
                        onClose()
                    }}
                />
                <Modal width="400px" onClick={event => event.stopPropagation()}>
                    <StyledModalHeader style={{border: 'none'}}>
                        <ModalTitle>{deniedTitle}</ModalTitle>
                        <StyledModalParagraph>{deniedParagraph}</StyledModalParagraph>
                    </StyledModalHeader>
                    <ModalFooter>
                        <Button variant="secondary" onClick={onClose}>
                            {t('commons:close')}
                        </Button>
                    </ModalFooter>
                </Modal>
            </>
        )
    }

    const onInputChange = debounce((event: ChangeEvent<HTMLInputElement>) => {
        if (event.target.value == 'Elimina') {
            setIsConfirmationValid(true)
        } else {
            setIsConfirmationValid(false)
        }
    }, 200)

    return (
        <>
            <ModalOverlay
                onClick={event => {
                    event.stopPropagation()
                    onClose()
                }}
            />
            <Modal width="400px" onClick={event => event.stopPropagation()}>
                <StyledModalHeader>
                    <ModalTitle>{title}</ModalTitle>
                    <div>
                        <StyledModalParagraph>{paragraph1}</StyledModalParagraph>
                        <StyledFullName>{fullName}</StyledFullName>
                        <StyledModalParagraph>{t('commons:delete_paragraph_2')}</StyledModalParagraph>
                    </div>
                </StyledModalHeader>
                <ModalBody>
                    <StyledModalParagraph>
                        <Trans i18nKey="commons:delete_confirm_label" components={{b: <b />}} />
                    </StyledModalParagraph>
                    <InputText
                        type="text"
                        placeholder={t('commons:delete_confirm_placeholder')}
                        onChange={onInputChange}
                    />
                </ModalBody>
                <ModalFooter>
                    <Button variant="secondary" onClick={onClose}>
                        {t('commons:cancel')}
                    </Button>
                    <Button
                        variant="primaryDanger"
                        disabled={deleteMutation.isPending || !isConfirmationValid}
                        onClick={() => deleteMutation.mutate()}
                    >
                        {t('commons:delete')}
                        {deleteMutation.isPending && <Spinner />}
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    )
}
